/**
 * The AI disclaimer (MDOT-25873): a one-line "Uses AI. Verify results." notice under the composer,
 * which opens the full disclaimer in a platform Dialog.
 *
 * The copy is compliance's, English in every locale by their decision, so it is deliberately not
 * routed through i18n. It lives in the composer slot (see SphereAIThread), which every surface that
 * renders the chat shares, so the notice cannot be missing from one of them.
 */
import { Dialog, Icon, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useState } from "react";

const DISCLAIMER_TITLE = "This chat uses AI models to answer your questions.";

const DISCLAIMER_PARAGRAPHS = [
	"To process your requests, Sphere shares the content needed to answer them with a third party LLM provider. Depending on the request, the information shared may include, without limitation, messages entered in the chat and any files attached to them, portfolio data in your account (including holdings, weights, composition and constraints), benchmarks and reference portfolios, market views and outlooks, analytics (including performance, risk and exposure figures), and data in instrument customisation and investment universes. Data is anonymised before being transmitted.",
	"Data is shared only when you submit a request in the chat. Each request is transmitted individually over an encrypted connection, together with the Sphere data relevant to it, and the response is returned to Sphere.",
	"The assistant operates within your existing Sphere permissions and can only access data that your user is already allowed to view. For this reason, answers to the same question may differ between users of the same organisation.",
	"Data shared to process your requests is not used to train AI models and is retained for up to 30 days, then deleted, unless longer retention is required by law or to enforce the LLM provider’s usage policies. Data is encrypted in transit (TLS) and at rest.",
	"Responses are generated probabilistically, based on your request and the data provided. They may therefore be inaccurate, incomplete or out of date, may sound plausible without being correct, and may not accurately reflect the underlying portfolio or market data.",
	"MDOTM does not train, fine-tune or otherwise modify the foundation models used by the chat. Operations performed through Sphere’s features, such as portfolio construction, optimisation, rebalancing and analytics, are carried out in accordance with the platform’s established processes and controls, including where they are initiated from the chat. Free-form responses generated by the AI, such as explanations, summaries and commentary, are produced automatically and are not individually reviewed by MDOTM; users should therefore verify them before relying on them for investment decisions.",
	"AI-generated answers do not constitute investment advice. Review responses before relying on them in investment decisions or sharing them in client communications, taking particular care where current and precise figures are required. Do not enter personal data that is not necessary for the request.",
	"The current LLM provider is Anthropic.",
];

const DISCLAIMER_VERSION = "1.0";
const DISCLAIMER_LAST_UPDATED = "26 October 2026";

export function SphereAIDisclaimer(): React.JSX.Element {
	const [open, setOpen] = useState(false);
	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="mx-auto flex items-center gap-1.5"
				data-qualifier="SphereAI/Disclaimer/Open"
			>
				<Icon icon="info" size={14} color={themeCSSVars.palette_N400} />
				<Text as="span" type="Body/S/Book" color={themeCSSVars.palette_N500}>
					Uses AI. Verify results.
				</Text>
				<svg
					width="14"
					height="14"
					viewBox="0 0 24 24"
					fill="none"
					stroke={themeCSSVars.palette_B500}
					strokeWidth="2"
					aria-hidden="true"
				>
					<path d="M14 4h6v6M20 4l-9 9M18 14v6H4V6h6" />
				</svg>
			</button>
			<Dialog
				show={open}
				onClose={() => setOpen(false)}
				size="large"
				header={DISCLAIMER_TITLE}
				footer={
					<Text as="p" type="Body/S/Book" color={themeCSSVars.palette_N400}>
						Version {DISCLAIMER_VERSION} · Last updated {DISCLAIMER_LAST_UPDATED}
					</Text>
				}
				data-qualifier="SphereAI/Disclaimer/Dialog"
			>
				<div className="flex flex-col gap-3">
					{DISCLAIMER_PARAGRAPHS.map((paragraph) => (
						<Text key={paragraph} as="p" type="Body/M/Book">
							{paragraph}
						</Text>
					))}
				</div>
			</Dialog>
		</>
	);
}
